import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

interface DeliveryTimePickerProps {
  onSelect: (dateTime: string) => void;
  onCancel?: () => void;
  minHoursFromNow?: number;
  /** Absolute floor; overrides minHoursFromNow when provided (e.g. branch opening time). */
  minDateTime?: Date | null;
  /** Absolute cutoff moment; selection cannot go past it. May fall on the day after the min day. */
  maxDateTime?: Date | null;
  value?: string;
  /** Picks the copy for the min-time hint ("Delivery available from" vs "Pickup available from"). */
  mode?: 'delivery' | 'pickup';
}

const ITEM_HEIGHT = 36;
const MINUTE_STEP = 5;

/**
 * Минимум округляем вверх до шага минут: 14:58 → 15:00. Иначе в первом часе
 * не остаётся ни одной минуты (58 → 60), и колонка падала на «00» — то есть
 * на 14:00, раньше разрешённого.
 */
const roundUpToStep = (date: Date) => {
  const rounded = new Date(date);
  rounded.setSeconds(0, 0);
  const minutes = rounded.getMinutes();
  const remainder = minutes % MINUTE_STEP;
  if (remainder || date.getSeconds() || date.getMilliseconds()) {
    rounded.setMinutes(minutes - remainder + MINUTE_STEP);
  }
  return rounded;
};
const VISIBLE_ITEMS = 3;

const ScrollColumn = ({
  items,
  selectedIndex,
  onSelect,
}: {
  items: string[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const dragState = useRef({
    startY: 0,
    startOffset: 0,
    isDragging: false,
    velocity: 0,
    lastY: 0,
    lastTime: 0,
  });
  const animFrame = useRef<number | null>(null);

  const maxOffset = (items.length - 1) * ITEM_HEIGHT;
  const padding = (ITEM_HEIGHT * (VISIBLE_ITEMS - 1)) / 2;

  const targetOffset = -selectedIndex * ITEM_HEIGHT;

  useEffect(() => {
    setOffset(targetOffset);
  }, []);

  useEffect(() => {
    if (offset !== -selectedIndex * ITEM_HEIGHT) {
      animateTo(-selectedIndex * ITEM_HEIGHT);
    }
  }, [selectedIndex]);

  const animateTo = useCallback((target: number) => {
    if (animFrame.current) cancelAnimationFrame(animFrame.current);
    const start = offset;
    const diff = target - start;
    const duration = 200;
    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setOffset(start + diff * eased);
      if (t < 1) {
        animFrame.current = requestAnimationFrame(step);
      } else {
        const clamped = Math.max(-maxOffset, Math.min(0, target));
        const newIndex = Math.round(-clamped / ITEM_HEIGHT);
        if (newIndex !== selectedIndex) {
          onSelect(newIndex);
        } else {
          setOffset(-newIndex * ITEM_HEIGHT);
        }
      }
    };
    animFrame.current = requestAnimationFrame(step);
  }, [offset, maxOffset, selectedIndex, onSelect]);

  const handlePointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    if (animFrame.current) cancelAnimationFrame(animFrame.current);
    dragState.current = {
      startY: e.clientY,
      startOffset: offset,
      isDragging: true,
      velocity: 0,
      lastY: e.clientY,
      lastTime: performance.now(),
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    e.stopPropagation();
    if (!dragState.current.isDragging) return;
    const dy = e.clientY - dragState.current.startY;
    let newOffset = dragState.current.startOffset + dy;
    newOffset = Math.max(-maxOffset, Math.min(0, newOffset));
    setOffset(newOffset);

    const now = performance.now();
    const dt = now - dragState.current.lastTime;
    if (dt > 0) {
      dragState.current.velocity = (e.clientY - dragState.current.lastY) / dt;
    }
    dragState.current.lastY = e.clientY;
    dragState.current.lastTime = now;
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    e.stopPropagation();
    if (!dragState.current.isDragging) return;
    dragState.current.isDragging = false;

    let projected = offset + dragState.current.velocity * 100;
    projected = Math.max(-maxOffset, Math.min(0, projected));
    const newIndex = Math.round(-projected / ITEM_HEIGHT);
    const clampedIndex = Math.max(0, Math.min(items.length - 1, newIndex));
    animateTo(-clampedIndex * ITEM_HEIGHT);
  };

  const handleClick = (index: number) => {
    if (dragState.current.isDragging) return;
    animateTo(-index * ITEM_HEIGHT);
  };

  const wasDragging = useRef(false);

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden select-none"
      style={{ height: ITEM_HEIGHT * VISIBLE_ITEMS, width: '70px', touchAction: 'none' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div
        style={{
          transform: `translateY(${offset + padding}px)`,
          willChange: 'transform',
        }}
      >
        {items.map((item, i) => {
          const centerIndex = Math.round(-offset / ITEM_HEIGHT);
          const isCenter = i === centerIndex;
          return (
            <div
              key={i}
              onPointerDown={() => { wasDragging.current = false; }}
              onClick={(e) => {
                e.stopPropagation();
                if (!wasDragging.current) handleClick(i);
              }}
              className={cn(
                'flex items-center justify-center cursor-pointer tabular-nums transition-all duration-150',
                isCenter
                  ? 'text-xl font-semibold text-text-primary opacity-100'
                  : 'text-base font-medium text-text-tertiary opacity-40'
              )}
              style={{ height: ITEM_HEIGHT }}
            >
              {item}
            </div>
          );
        })}
      </div>
      <div
        className="absolute left-0 right-0 pointer-events-none border-t border-b border-border"
        style={{
          top: padding,
          height: ITEM_HEIGHT,
        }}
      />
    </div>
  );
};

export const DeliveryTimePicker = ({
  onSelect,
  onCancel,
  minHoursFromNow = 1,
  minDateTime,
  maxDateTime,
  value,
  mode = 'delivery',
}: DeliveryTimePickerProps) => {
  const { t } = useTranslation();
  const now = new Date();

  const minTime = roundUpToStep(
    minDateTime ?? new Date(now.getTime() + minHoursFromNow * 60 * 60 * 1000)
  );
  const maxTime = maxDateTime ?? null;
  const minDateStr = format(minTime, 'yyyy-MM-dd');
  const maxDateStr = maxTime ? format(maxTime, 'yyyy-MM-dd') : null;

  // One continuous run of hour slots from minTime up to maxTime (or to the
  // end of minTime's day when there's no cap), so scrolling past midnight
  // just keeps going instead of needing a separate day switch.
  const slots = (() => {
    const cursor = new Date(minTime);
    cursor.setMinutes(0, 0, 0);
    const end = maxTime ? new Date(maxTime) : new Date(minTime);
    end.setMinutes(0, 0, 0);
    if (!maxTime) end.setHours(23);
    const result: { date: string; hour: number }[] = [];
    while (cursor <= end) {
      result.push({ date: format(cursor, 'yyyy-MM-dd'), hour: cursor.getHours() });
      cursor.setHours(cursor.getHours() + 1);
    }
    return result.length ? result : [{ date: minDateStr, hour: minTime.getHours() }];
  })();

  const minutesForSlot = (slotIndex: number) => {
    const isFirst = slotIndex === 0;
    const isLast = slotIndex === slots.length - 1;
    const lowerMinute = isFirst ? minTime.getMinutes() : 0;
    const upperMinute = isLast && maxTime ? maxTime.getMinutes() : 59;
    return Array.from({ length: 12 }, (_, i) => i * 5)
      .filter(
        m => m >= Math.ceil(lowerMinute / 5) * 5 && m <= Math.floor(upperMinute / 5) * 5
      )
      .map(m => m.toString().padStart(2, '0'));
  };

  const parsedValue = (() => {
    if (!value) return null;
    const [datePart, timePart] = value.split(' ');
    const [h, m] = timePart.split(':').map(Number);
    return { date: datePart, hour: h, minute: m };
  })();

  const initialSlotIndex = parsedValue
    ? Math.max(
        0,
        slots.findIndex(s => s.date === parsedValue.date && s.hour === parsedValue.hour)
      )
    : 0;

  const [selectedSlotIndex, setSelectedSlotIndex] = useState(initialSlotIndex);

  const initialMinutes = minutesForSlot(initialSlotIndex);
  const savedMinute =
    parsedValue && slots[initialSlotIndex]?.date === parsedValue.date && slots[initialSlotIndex]?.hour === parsedValue.hour
      ? (Math.round(parsedValue.minute / 5) * 5) % 60
      : null;
  // Ранее выбранное время могло устареть: минимум с тех пор сдвинулся вперёд.
  // Берём сохранённую минуту, только если она всё ещё допустима.
  const initialMinute =
    savedMinute !== null &&
    initialMinutes.includes(savedMinute.toString().padStart(2, '0'))
      ? savedMinute
      : parseInt(initialMinutes[0] ?? '0');

  const [selectedMinute, setSelectedMinute] = useState(initialMinute);

  const minutes = minutesForSlot(selectedSlotIndex);
  const selectedSlot = slots[selectedSlotIndex];

  // Crossing into the first or last slot can strand the previously selected
  // minute outside the now-valid range — snap it back in bounds.
  const selectSlot = (newIndex: number) => {
    setSelectedSlotIndex(newIndex);
    const validMinutes = minutesForSlot(newIndex);
    const minuteStr = selectedMinute.toString().padStart(2, '0');
    if (validMinutes.length && !validMinutes.includes(minuteStr)) {
      const tooLow = newIndex === 0 && selectedMinute < minTime.getMinutes();
      setSelectedMinute(
        parseInt(tooLow ? validMinutes[0] : validMinutes[validMinutes.length - 1])
      );
    }
  };

  const minTimeStr = format(minTime, 'HH:mm');
  const maxTimeStr = maxTime ? format(maxTime, 'HH:mm') : null;
  const hourStr = selectedSlot.hour.toString().padStart(2, '0');
  const minuteStr = selectedMinute.toString().padStart(2, '0');
  const selectedTimeStr = `${hourStr}:${minuteStr}`;

  // Последний рубеж: что бы ни показывали колонки, раньше минимума и позже
  // максимума не отдаём — подставляем ближайшую допустимую границу.
  const handleConfirm = () => {
    const [year, month, day] = selectedSlot.date.split('-').map(Number);
    let picked = new Date(year, month - 1, day, selectedSlot.hour, selectedMinute);
    if (picked < minTime) picked = minTime;
    if (maxTime && picked > maxTime) picked = maxTime;
    onSelect(format(picked, 'yyyy-MM-dd HH:mm:00'));
  };

  const adjustSlot = (delta: number) => {
    const newIdx = Math.max(0, Math.min(slots.length - 1, selectedSlotIndex + delta));
    selectSlot(newIdx);
  };

  const adjustMinute = (delta: number) => {
    const currentIdx = minutes.indexOf(minuteStr);
    const newIdx = Math.max(0, Math.min(minutes.length - 1, currentIdx + delta));
    setSelectedMinute(parseInt(minutes[newIdx]));
  };

  return (
    <div data-testid="time-picker" className="flex flex-col gap-3 pb-2 pt-1">
      {/* Time display */}
      <div className="flex items-center justify-center py-2">
        <span
          data-testid="time-picker-value"
          className="text-2xl font-semibold text-text-primary tabular-nums"
        >
          {selectedTimeStr}
        </span>
      </div>

      {/* Scroll picker */}
      <div className="flex items-stretch gap-2 px-2">
        {/* Hours — one continuous scroll across the whole valid range, so it
            just keeps going past midnight instead of needing a day switch. */}
        <div className="flex flex-col items-center flex-1">
          <button
            data-testid="time-picker-hour-up"
            onClick={() => adjustSlot(1)}
            className="w-10 h-8 flex items-center justify-center rounded-lg text-text-tertiary active:text-text-primary mb-1 transition-colors"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <ScrollColumn
            items={slots.map(s => s.hour.toString().padStart(2, '0'))}
            selectedIndex={selectedSlotIndex}
            onSelect={selectSlot}
          />
          <button
            data-testid="time-picker-hour-down"
            onClick={() => adjustSlot(-1)}
            className="w-10 h-8 flex items-center justify-center rounded-lg text-text-tertiary active:text-text-primary mt-1 transition-colors"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <span className="text-caption-xs-semibold text-text-tertiary mt-1.5">
            {t('hours')}
          </span>
        </div>

        <div className="flex items-center justify-center pb-4">
          <span className="text-xl font-semibold text-text-tertiary">:</span>
        </div>

        {/* Minutes */}
        <div className="flex flex-col items-center flex-1">
          <button
            data-testid="time-picker-minute-up"
            onClick={() => adjustMinute(5)}
            className="w-10 h-8 flex items-center justify-center rounded-lg text-text-tertiary active:text-text-primary mb-1 transition-colors"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <ScrollColumn
            items={minutes}
            selectedIndex={minutes.indexOf(selectedMinute.toString().padStart(2, '0'))}
            onSelect={(i) => setSelectedMinute(parseInt(minutes[i]))}
          />
          <button
            data-testid="time-picker-minute-down"
            onClick={() => adjustMinute(-5)}
            className="w-10 h-8 flex items-center justify-center rounded-lg text-text-tertiary active:text-text-primary mt-1 transition-colors"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <span className="text-caption-xs-semibold text-text-tertiary mt-1.5">
            {t('minutes')}
          </span>
        </div>
      </div>

      <div className="flex flex-col items-center gap-0.5">
        {selectedSlot.date === minDateStr && (
          <p
            data-testid="time-picker-min-hint"
            className="text-caption-sm-medium text-text-tertiary text-center"
          >
            {t(mode === 'pickup' ? 'pickup_available_from' : 'delivery_available_from')} {minTimeStr}
          </p>
        )}
        {maxTimeStr && selectedSlot.date === maxDateStr && (
          <p
            data-testid="time-picker-max-hint"
            className="text-caption-sm-medium text-text-tertiary text-center"
          >
            {t('available_until')} {maxTimeStr}
          </p>
        )}
      </div>

      {/* Buttons */}
      <div className="flex gap-2">
        {onCancel && (
          <Button
            data-testid="time-picker-cancel"
            onClick={onCancel}
            variant="outline"
            className="flex-1 h-12 border-border text-text-primary rounded-xl"
          >
            {t('cancel')}
          </Button>
        )}
        <Button
          data-testid="time-picker-confirm"
          onClick={handleConfirm}
          className="flex-1 h-12 bg-button-primary text-white rounded-xl"
        >
          {t('accept')}
        </Button>
      </div>
    </div>
  );
};
